import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { useState, useContext } from 'react';
import { Alert, Button, Checkbox, DataGrid, FormControlLabel, Grid, GridColumn, GridRow, Icon, IconButton, LinearProgress, Link, MenuItem, Modal, ModalBody, ModalFooter, Select, useSnackbar, Spacer, TextField, Typography } from '@emburse/embark-core';
import { AddIcon, EditIcon } from '@emburse/embark-icons';
import { IconMenu, InputPassword } from '../../../components';
import { createNewCredentials, getCredentialsByHomeFolder } from '../../../api/ftpTodayAPI';
import { createExistingCredentials, removeExistingCredentials, createNewFluffyname, createNewPassword, updateCredentials } from '../../../api/sftp';
import { SFTPConfigContext } from '../SFTPConfigProvider';
import { $t, useTranslation } from '../../../hooks/useTranslation';
import { formatDate } from '../../../util/dateAdapter';
function CredentialsTable({ loading, createAllowed, isCertifyAdmin, credentials, sftpPaths, updateData }) {
    useTranslation();
    const RootDirRegex = /^\/DI\/\w+\/$|^\/DI_\w+\/$/;
    /** Context */
    const { searched, setSearched } = useContext(SFTPConfigContext);
    const handleSearch = (e, value) => setSearched(value);
    /** Modal Options */
    const [searchCredentialsModalOpen, setSearchCredentialsModalOpen] = useState(false);
    const [searchCredentialsModalLoading, setSearchCredentialsModalLoading] = useState(false);
    const [addCredentialsModalOpen, setAddCredentialsModalOpen] = useState(false);
    const [addCredentialsModalLoading, setAddCredentialsModalLoading] = useState(false);
    const [hasNewPassword, setHasNewPassword] = useState(false);
    const [modifyCredentialModalOpen, setModifyCredentialModalOpen] = useState(false);
    const [allowCredentialSave, setAllowCredentialSave] = useState(false);
    /** Form Actions */
    const [foundCredentials, setFoundCredentials] = useState([]);
    const [selectedCredentials, setSelectedCredentials] = useState(null);
    const [selectedPath, setSelectedPath] = useState('');
    const [warnCredentialsUnsaved, setWarnCredentialsUnsaved] = useState({
        Error: false,
        Message: ''
    });
    const transformCredentials = (row) => row;
    const { enqueueSnackbar } = useSnackbar();
    const updateWarnCredentialsUnsaved = (error, message) => {
        setWarnCredentialsUnsaved({
            Error: error,
            Message: message
        });
    };
    const handleSelectedPathChange = (Path) => {
        setSelectedPath(Path);
        setSearchCredentialsModalLoading(true);
        getCredentialsByHomeFolder(Path).then((resp) => {
            const data = JSON.parse(resp.Data);
            setFoundCredentials([...data.map(transformCredentials)]);
            setSearchCredentialsModalLoading(false);
            setSearched(true);
        });
    };
    const handleAddExistingAccount = (id, username) => {
        // Need to have ID and Username to confirm addition of account (do we need to expand on this security?
        // I feel these two bits will prevent random additions by brute force)
        createExistingCredentials(id, username).then((resp) => {
            if (resp.Status === 'success') {
                updateData();
                setSearchCredentialsModalOpen(false);
                enqueueSnackbar($t('Credentials have been added!'), {
                    variant: 'alert',
                    AlertProps: { severity: 'success' }
                });
            }
            else {
                enqueueSnackbar(resp.Message, {
                    variant: 'alert',
                    AlertProps: { severity: 'error' }
                });
            }
        });
    };
    const handleUpdateCredentials = () => {
        updateCredentials(selectedCredentials).then((resp) => {
            if (resp.Status === 'success') {
                updateData();
                setSearchCredentialsModalOpen(false);
                setAllowCredentialSave(false);
                setHasNewPassword(false);
                updateWarnCredentialsUnsaved(false, '');
                enqueueSnackbar($t('Credentials have been added!'), {
                    variant: 'alert',
                    AlertProps: { severity: 'success' }
                });
            }
            else {
                enqueueSnackbar(resp.Message, {
                    variant: 'alert',
                    AlertProps: { severity: 'error' }
                });
            }
        });
    };
    const handleCreateCredentials = () => {
        setAddCredentialsModalLoading(true);
        createNewCredentials(selectedCredentials).then((resp) => {
            if (resp.Status === 'success') {
                setAddCredentialsModalLoading(false);
                setAddCredentialsModalOpen(false);
                updateData();
                enqueueSnackbar($t('Credentials successfully created'), {
                    variant: 'alert',
                    AlertProps: { severity: 'success' }
                });
            }
            else {
                enqueueSnackbar(resp.Message, {
                    variant: 'alert',
                    AlertProps: { severity: 'error' }
                });
                setAddCredentialsModalLoading(false);
            }
        });
    };
    const handleCreateCredentialsClick = () => {
        if (isCertifyAdmin) {
            enqueueSnackbar($t('Credentials can only be created by client administrators.'), {
                variant: 'alert',
                AlertProps: { severity: 'error' }
            });
            return;
        }
        createNewFluffyname().then((resp) => {
            if (resp.Status === 'success') {
                const username = JSON.parse(resp.Data);
                createNewPassword().then((response) => {
                    if (response.Status === 'success') {
                        const password = JSON.parse(response.Data);
                        setSelectedCredentials({
                            hostname: 'sftp.certify.com',
                            username,
                            password,
                            port: '22',
                            rootDirectory: `/DI/${username}`
                        });
                        setAddCredentialsModalOpen(true);
                    }
                    else {
                        enqueueSnackbar(response.Message, {
                            variant: 'alert',
                            AlertProps: { severity: 'error' }
                        });
                    }
                });
            }
            else {
                enqueueSnackbar(resp.Message, {
                    variant: 'alert',
                    AlertProps: { severity: 'error' }
                });
            }
        });
    };
    const GenerateNewPassword = () => {
        if (isCertifyAdmin) {
            enqueueSnackbar($t('Passwords can only be reset by client administrators.'), {
                variant: 'alert',
                AlertProps: { severity: 'error' }
            });
            return;
        }
        const objCreds = { ...selectedCredentials };
        createNewPassword().then((resp) => {
            objCreds.password = JSON.parse(resp.Data);
            setSelectedCredentials(objCreds);
            updateWarnCredentialsUnsaved(true, $t('Password not confirmed until youd click Save.'));
            setAllowCredentialSave(true);
            setHasNewPassword(true);
        });
    };
    const RemoveCredentials = () => {
        const objCreds = { ...selectedCredentials };
        removeExistingCredentials(objCreds.username, objCreds.hostname).then((resp) => {
            if (resp.Status === 'success') {
                enqueueSnackbar($t('SFTP Credentials have been removed from Certify.'), {
                    variant: 'alert',
                    AlertProps: { severity: 'success' }
                });
                setModifyCredentialModalOpen(false);
                updateData();
            }
            else {
                enqueueSnackbar(resp.Message, {
                    variant: 'alert',
                    AlertProps: { severity: 'error' }
                });
            }
        });
    };
    return (_jsxs(_Fragment, { children: [!createAllowed && !isCertifyAdmin && (_jsx(Alert, { severity: "info", children: $t("Your company's account does not have this feature activated, please reach out to Support for further assistance.") })), (createAllowed || isCertifyAdmin) && (_jsxs(_Fragment, { children: [_jsxs(GridRow, { justifyContent: "space-between", alignItems: "center", children: [_jsx(Grid, { item: true, children: _jsx(Typography, { variant: "h5", children: $t('Credentials') }) }), _jsxs(Grid, { item: true, children: [isCertifyAdmin && (_jsxs(_Fragment, { children: [_jsx(Button, { disabled: loading, variant: "outlined", onClick: () => {
                                                    setSearchCredentialsModalOpen(true);
                                                }, children: $t('ADD EXISTING') }), _jsx(Spacer, { size: 10, direction: "horizontal" })] })), !credentials.length && (_jsx(Button, { disabled: !!(credentials.filter((cred) => cred.hostname.toLowerCase() ===
                                            'sftp.certify.com').length > 0 || loading === true), onClick: () => {
                                            handleCreateCredentialsClick();
                                        }, children: $t('CREATE') }))] })] }), _jsx(Spacer, { size: 16 }), _jsx(DataGrid, { loading: loading, autoHeight: true, hideFooterPagination: true, columns: [
                            {
                                field: 'hostname',
                                headerName: $t('Hostname'),
                                flex: 1,
                                renderCell: (cell) => _jsx(Typography, { children: cell.value })
                            },
                            {
                                field: 'username',
                                headerName: $t('Username'),
                                flex: 1,
                                renderCell: (cell) => _jsx(Typography, { children: cell.value })
                            },
                            {
                                field: 'lastLogin',
                                headerName: $t('Last Login'),
                                flex: 1,
                                renderCell: (cell) => (_jsx(Typography, { children: formatDate(cell.value) }))
                            },
                            {
                                field: 'lastPasswordReset',
                                headerName: $t('Last Password Reset'),
                                flex: 1,
                                renderCell: (cell) => (_jsx(Typography, { children: formatDate(cell.value) }))
                            },
                            {
                                field: 'action',
                                headerName: ' ',
                                width: 56,
                                renderCell: (cell) => (_jsx("div", { style: {
                                        width: '100%'
                                    }, children: _jsx(IconButton, { size: "medium", title: $t('Edit credentials'), onClick: () => {
                                            setSelectedCredentials(cell.row);
                                            setModifyCredentialModalOpen(true);
                                        }, children: _jsx(Icon, { iconUrl: EditIcon }) }) }))
                            }
                        ], rows: credentials, localeText: {
                            noRowsLabel: $t('There are currently no credentials to be managed.')
                        } })] })), _jsx(Modal, { open: searchCredentialsModalOpen, onClose: () => setSearchCredentialsModalOpen(false), modalHeader: $t('Search For Existing Credentials'), size: "sm", children: _jsxs(ModalBody, { children: [_jsx(Spacer, { size: 8 }), sftpPaths.filter((folder) => RootDirRegex.test(folder.Path)).length === 0 && (_jsxs(_Fragment, { children: [_jsx(Typography, { variant: "body1", children: $t(`No existing SFTP paths or credentials were found in the system.
                                Before enabling, please search Zendesk for any existing, unconfigured credentials.`) }), _jsx(Spacer, { size: 16 }), _jsx(FormControlLabel, { value: searched, control: _jsx(Checkbox, { id: "confirmSearch", style: { marginTop: '-12px' }, color: "primary", onChange: handleSearch }), style: { alignItems: 'flex-start' }, label: _jsx(Typography, { variant: "body2", children: $t('I agree that I have searched for previously created credentials.') }) })] })), sftpPaths.filter((folder) => RootDirRegex.test(folder.Path)).length > 0 && (_jsx(Select, { id: "path-select", label: $t('Root Directory'), value: selectedPath, onChange: (e) => handleSelectedPathChange(e.target.value), helperText: $t('Search a root directory for existing SFTP credentials'), fullWidth: true, children: sftpPaths
                                .filter((folder) => RootDirRegex.test(folder.Path))
                                .map((folder) => (_jsx(MenuItem, { value: folder.Path, children: folder.Path }, folder.Path))) })), selectedPath.length > 0 && (_jsxs(_Fragment, { children: [_jsx(Spacer, { size: 16 }), _jsx(DataGrid, { loading: searchCredentialsModalLoading, autoHeight: true, hideFooterPagination: true, hideFooter: true, rows: foundCredentials, columns: [
                                        {
                                            field: 'username',
                                            headerName: $t('Username'),
                                            flex: 1,
                                            renderCell: (cell) => (_jsx(Typography, { children: cell.row.attributes.username }))
                                        },
                                        {
                                            field: 'last_login',
                                            headerName: $t('Last Login'),
                                            flex: 1,
                                            renderCell: (cell) => (_jsx(Typography, { children: formatDate(cell.row.attributes.last_login) }))
                                        },
                                        {
                                            field: 'Action',
                                            headerName: ' ',
                                            width: 56,
                                            renderCell: (cell) => (_jsx("div", { style: {
                                                    width: '100%',
                                                    textAlign: 'center'
                                                }, children: _jsx(IconMenu, { title: $t('Add Account'), icon: AddIcon, onOpen: () => handleAddExistingAccount(cell.row.id, cell.row.attributes.username), children: _jsx(MenuItem, { style: { width: '96px ' }, onClick: () => {
                                                            setModifyCredentialModalOpen(true);
                                                        }, children: $t('Edit') }) }) }))
                                        }
                                    ], localeText: {
                                        noRowsLabel: $t('No existing credentials found for selected path.')
                                    } })] }))] }) }), _jsx(Spacer, { size: 32 }), selectedCredentials && (_jsxs(_Fragment, { children: [_jsx(Modal, { open: modifyCredentialModalOpen, onClose: () => setModifyCredentialModalOpen(false), modalHeader: $t('Edit Credentials'), size: "sm", modalFooter: _jsx(ModalFooter, { children: _jsxs(GridRow, { spacing: 1, justifyContent: "flex-end", alignItems: "center", children: [_jsx(Grid, { item: true, children: _jsx(Button, { disabled: !allowCredentialSave, color: "primary", onClick: () => {
                                                handleUpdateCredentials();
                                                setModifyCredentialModalOpen(false);
                                            }, children: $t('Save') }) }), _jsx(Grid, { item: true, children: _jsx(Button, { color: "monochrome", onClick: () => {
                                                setModifyCredentialModalOpen(false);
                                            }, children: $t('Close') }) })] }) }), children: _jsx(ModalBody, { style: { overflow: 'visible' }, children: _jsxs(GridColumn, { spacing: 3, alignItems: "stretch", children: [_jsx(Grid, { item: true, children: _jsx(TextField, { label: $t('Hostname'), fullWidth: true, value: selectedCredentials.hostname, disabled: selectedCredentials.hostname === 'sftp.certify.com' }) }), _jsx(Grid, { item: true, children: _jsx(TextField, { label: $t('Port'), fullWidth: true, value: selectedCredentials.port, disabled: selectedCredentials.hostname === 'sftp.certify.com' }) }), _jsx(Grid, { item: true, children: _jsx(TextField, { label: $t('Username'), fullWidth: true, value: selectedCredentials.username, disabled: selectedCredentials.hostname === 'sftp.certify.com' }) }), hasNewPassword && (_jsx(Grid, { item: true, children: _jsx(InputPassword, { label: $t('Password'), fullWidth: true, value: selectedCredentials.password, error: warnCredentialsUnsaved.Error, helperText: warnCredentialsUnsaved.Error
                                                ? warnCredentialsUnsaved.Message
                                                : '', disabled: true }) })), !hasNewPassword && (_jsx(Grid, { item: true, children: _jsx(TextField, { label: $t('Password'), fullWidth: true, type: "password", disabled: selectedCredentials.hostname === 'sftp.certify.com' }) })), _jsx(Grid, { item: true, children: _jsx(Link, { onClick: () => {
                                                GenerateNewPassword();
                                            }, children: $t('GENERATE NEW PASSWORD') }) }), isCertifyAdmin && (_jsx(Grid, { item: true, children: _jsx(Link, { onClick: () => {
                                                RemoveCredentials();
                                            }, children: $t('REMOVE CREDENTIALS') }) }))] }) }) }), _jsx(Modal, { open: addCredentialsModalOpen, onClose: () => setAddCredentialsModalOpen(false), modalHeader: $t('Create Credentials'), size: "sm", modalFooter: _jsx(ModalFooter, { children: _jsxs(GridRow, { spacing: 1, justifyContent: "flex-end", alignItems: "center", children: [_jsx(Grid, { item: true, children: _jsx(Button, { disabled: addCredentialsModalLoading, color: "primary", onClick: () => {
                                                handleCreateCredentials();
                                            }, children: $t('Create') }) }), _jsx(Grid, { item: true, children: _jsx(Button, { color: "monochrome", onClick: () => {
                                                setAddCredentialsModalOpen(false);
                                            }, children: $t('Cancel') }) })] }) }), children: _jsxs(ModalBody, { children: [addCredentialsModalLoading && (_jsxs(_Fragment, { children: [_jsx(Grid, { item: true, children: _jsx(LinearProgress, { color: "primary", variant: "indeterminate" }) }), _jsx(Spacer, { size: 8 })] })), _jsxs(GridColumn, { spacing: 3, alignItems: "stretch", children: [_jsx(Grid, { item: true, children: _jsx(TextField, { label: $t('Hostname'), fullWidth: true, value: selectedCredentials.hostname, disabled: selectedCredentials.hostname === 'sftp.certify.com' }) }), _jsx(Grid, { item: true, children: _jsx(TextField, { label: $t('Port'), fullWidth: true, value: selectedCredentials.port, disabled: selectedCredentials.hostname === 'sftp.certify.com' }) }), _jsx(Grid, { item: true, children: _jsx(TextField, { label: $t('Username'), fullWidth: true, value: selectedCredentials.username, disabled: selectedCredentials.hostname === 'sftp.certify.com' }) }), _jsx(Grid, { item: true, children: _jsx(InputPassword, { label: $t('Password'), fullWidth: true, value: selectedCredentials.password.length > 0
                                                    ? selectedCredentials.password
                                                    : 'Unknown', error: warnCredentialsUnsaved.Error, helperText: warnCredentialsUnsaved.Error
                                                    ? warnCredentialsUnsaved.Message
                                                    : '', disabled: true }) }), _jsx(Grid, { item: true, children: _jsx(TextField, { label: $t('Home Directory'), fullWidth: true, value: selectedCredentials.rootDirectory, disabled: selectedCredentials.hostname === 'sftp.certify.com' }) })] })] }) })] }))] }));
}
export default CredentialsTable;
